import React from 'react';
import { FaShieldAlt, FaBell, FaHeadset, FaUserCircle } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { Link } from "react-router-dom";
import webInterface from "../assets/interface.png";

function HomePage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-900 to-blue-700 text-white py-24">
        <div className="absolute inset-0 bg-black opacity-40"></div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex flex-col md:flex-row items-center justify-between"
          >
            <div className="text-center md:text-left md:w-1/2 mb-10 md:mb-0">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Top Up Your Balance <br />
                <span className="text-yellow-400">Instantly & Securely</span>
              </h1>
              <p className="text-xl mb-8 text-gray-200">
                Experience the fastest and most secure way to recharge your balance. 
                Anytime, anywhere, with just a few taps.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <Link to="/login">
                <button className="bg-yellow-400 text-blue-900 px-8 py-3 rounded-full font-bold hover:bg-yellow-300 transition duration-300">
                  Get Started
                </button>
                </Link>
                <Link to="/about">
                <button className="border-2 border-white px-8 py-3 rounded-full font-bold hover:bg-white hover:text-blue-900 transition duration-300">
                  Learn More
                </button>
                </Link>
              </div>
            </div>
            
            <div className="md:w-1/2 flex justify-center">
              <motion.img 
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.8 }}
                src={webInterface} 
                alt="VTU App Interface"
                className="w-full max-w-md"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Why Choose Our VTU App?
            </h2>
            <p className="text-gray-600 text-lg">
              Experience the best features designed for your convenience
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <FaShieldAlt className="text-4xl text-blue-600" />,
                title: "Secure Transactions",
                description: "Bank-grade encryption for all your transactions"
              },
              {
                icon: <FaBell className="text-4xl text-blue-600" />,
                title: "Instant Notifications",
                description: "Real-time updates on all your transactions"
              },
              {
                icon: <FaHeadset className="text-4xl text-blue-600" />,
                title: "24/7 Support",
                description: "Round-the-clock assistance whenever you need it"
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                whileHover={{ y: -10 }}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex flex-col items-center text-center">
                  {feature.icon}
                  <h3 className="text-xl font-bold mt-4 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-blue-50 py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-16">
            What Our Users Say
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                text: "The fastest and most reliable VTU service I've ever used. Absolutely recommended!",
                author: "Emmanuel Oluyemi",
                role: "Regular User"
              },
              {
                text: "Simple, secure, and instant top-ups. This app has made my life so much easier!",
                author: "Praise Adebayo",
                role: "Business Owner"
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-xl shadow-lg"
              >
                <p className="text-gray-600 italic mb-4">{testimonial.text}</p>
                <div className="flex items-center">
                  <FaUserCircle className="text-blue-600 text-3xl mr-3" />
                  <div>
                    <p className="font-bold text-gray-800">{testimonial.author}</p>
                    <p className="text-gray-500 text-sm">{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-12 text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-xl mb-8">
              Join thousands of satisfied users today
            </p>
            <button className="bg-yellow-400 text-blue-900 px-8 py-3 rounded-full font-bold hover:bg-yellow-300 transition duration-300">
              Download Now
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;